/**
 * Componente de notificaciones toast
 * Muestra mensajes de éxito, error, advertencia e información
 * @module components/ui/NotificationToast
 */

import { useEffect, useState } from 'react'
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react'
import { useUIStore } from '@/stores'

interface ToastItemProps {
  id: string
  type: 'info' | 'success' | 'warning' | 'error'
  message: string
  onClose: () => void
}

function ToastItem({ type, message, onClose }: ToastItemProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [isExiting, setIsExiting] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 10)
    return () => clearTimeout(timer)
  }, [])

  const handleClose = () => {
    setIsExiting(true)
    setTimeout(onClose, 300)
  }

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />
      case 'info':
      default:
        return <Info className="w-5 h-5 text-blue-500" />
    }
  }

  const getStyles = () => {
    const base = 'border-l-4'
    switch (type) {
      case 'success':
        return `${base} border-green-500 bg-green-50 dark:bg-green-900/20`
      case 'error':
        return `${base} border-red-500 bg-red-50 dark:bg-red-900/20`
      case 'warning':
        return `${base} border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20`
      case 'info':
      default:
        return `${base} border-blue-500 bg-blue-50 dark:bg-blue-900/20`
    }
  }

  return (
    <div
      className={`
        flex items-start gap-3 p-4 rounded-lg shadow-lg
        ${getStyles()}
        transform transition-all duration-300 ease-out
        ${isVisible && !isExiting ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
    >
      <div className="flex-shrink-0 mt-0.5">
        {getIcon()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words">
          {message}
        </p>
      </div>
      <button
        onClick={handleClose}
        className="flex-shrink-0 p-1 rounded hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
      >
        <X className="w-4 h-4 text-gray-500" />
      </button>
    </div>
  )
}

export function NotificationToast() {
  const notifications = useUIStore((state) => state.notifications)
  const removeNotification = useUIStore((state) => state.removeNotification)

  if (notifications.length === 0) return null

  return (
    <div className="fixed top-20 right-4 z-[9999] flex flex-col gap-2 max-w-sm w-full pointer-events-none">
      {notifications.map((notification) => (
        <div key={notification.id} className="pointer-events-auto">
          <ToastItem
            id={notification.id}
            type={notification.type}
            message={notification.message}
            onClose={() => removeNotification(notification.id)}
          />
        </div>
      ))}
    </div>
  )
}
